import React, { useState, Fragment, useEffect, useContext } from 'react';
import { Badge, Button } from "reactstrap";
import { createThirdwebClient, getContract, prepareContractCall } from "thirdweb";
import {
    ConnectButton,
    TransactionButton,
    useActiveWalletConnectionStatus,
    useActiveAccount,
    useWalletBalance,
    useActiveWallet
} from "thirdweb/react";
import { createWallet } from "thirdweb/wallets";
import { sepolia } from "thirdweb/chains"
import { ContractAbi } from '../../../contract/Abi';
import { THIRDWEBConstants } from '../../../constants/thirdweb';
import { ContractConstants } from '../../../constants/contract';
import useSolution from '../../../hooks/useSolution';
import { ActionConfirm } from './ActionConfirm';
import { PixelContext } from '../../../context/Pixel';
import { ProjectContext } from '../../../context/Project';

/**
 * Blockchain Confirm Work Completion component
 * 
 * Executed by Homeowner after both parties agree
 * that the work is complete.
 * Transfers ETH from the contract to the contractor
 * and Homeowner as it was agreed.
 */
export function ConfirmWorkCompletion({contractBc, solution, onAction, onSuccess, onError}) {
    const [waitingForWallet, setWaitingForWallet] = useState(false);
    const [showConfirmWorkCompletionNotice, setShowConfirmWorkCompletionNotice] = useState(false);

    const client = createThirdwebClient({ clientId: THIRDWEBConstants.CLIENT_ID });
    const wallets = [createWallet("io.metamask")];
    const wallet = useActiveWallet();
    const walletChain = wallet?.getChain();
    const activeAccount = useActiveAccount();
    const walletAddress = activeAccount?.address;
    const { data } = useWalletBalance({client, chain: walletChain, address: walletAddress});
    const balance = data;
    const walletConnectionStatus = useActiveWalletConnectionStatus();
    const walletName = wallet?.walletName;
    const walletConnected = walletConnectionStatus === 'connected';
    const walletsMatch = activeAccount?.address.toLowerCase() === contractBc?.contract.homeowner?.toLowerCase();
    const solutionService = useSolution();
    const { MetaPixel } = useContext(PixelContext);
    const { getTotalBudget } = useContext(ProjectContext);

    useEffect(() => {
        if (typeof onSuccess === 'function') {
            if (solutionService.solution || solutionService.error) {
                onSuccess(solutionService.solution);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [solutionService.solution, solutionService.error]);

    const getPreparedConfirmWorkCompletionContractCall = () => {
        const contract = getContract({
            client,
            chain: sepolia,
            address: contractBc?.contract?.address,
            abi: ContractAbi
        });

        return prepareContractCall({
            contract,
            method: ContractConstants.METHOD.CONFIRM_WORK_COMPLETION,
            params: []
        });
    }

    const showTransactionConfirmMessageButton = (show) => {
        showElementByClassName('transaction-confirm-message', show);
    }

    const showConfirmWorkCompletionActionButton = (show) => {
        showElementByClassName('confirm-work-completion-action', show);
    }

    const getElementByClassName = (className) => {
        return document.getElementsByClassName(className)[0];
    }

    const showElementByClassName = (className, show) => {
        let elem = getElementByClassName(className)

        if (elem !== undefined) {
            elem.style.display = (show ? 'inline-flex' : 'none');
        }
    }

    return (
        <Fragment>
            <div className='col-12 col-md-6'>
                {
                    !walletConnected ? (
                        "Please connect your wallet to continue"
                    ) : (
                        walletsMatch ? (
                            <Fragment>
                                <div>
                                    <span className='fw-bold'>Confirm work Completion</span>
                                </div>
                                <div>
                                    <span>Contract balance: </span>
                                    <span className='text-success'>{ contractBc?.contract?.balance } </span>
                                    <span className='text-success'>{ balance?.symbol }</span>
                                </div>
                                <div>
                                    <span>Amount to transfer to Contractor: </span>
                                    <span className='text-success'>{ contractBc?.contract?.amountToContractor } </span>
                                    <span className='text-success'>{ balance?.symbol }</span>
                                </div>
                                <div className='d-none'>
                                    <span>Amount to transfer to Homeowner: </span>
                                    <span className='text-success'>{ contractBc?.contract?.amountToHomeowner } </span>
                                    <span className='text-success'>{ balance?.symbol }</span>
                                </div>
                                <Badge color="secondary" className='d-none text-wrap text-start w-100'>
                                    This action will set the Contract as Completed and transfer agreed
                                    amounts to Contractor and Homeowner.
                                </Badge>
                                <Badge color="secondary" className='text-wrap text-start w-100'>
                                    This action will set the Contract as Completed and transfer the 100% 
                                    of the contract balance to the Contractor.
                                </Badge>
                            </Fragment>
                        ) : (
                            "Please switch to the correct account to continue."
                        )
                    )
                }
            </div>
            <div className='col-sm-6 d-table text-center'>
                {
                    walletsMatch ? (
                        <>
                            <div className='d-table-cell align-middle'>
                                <TransactionButton
                                    theme={'light'}
                                    style={{ display: 'none' }}
                                    className='mt-2 mb-2 confirm-work-completion-action'
                                    transaction={() => {
                                        return getPreparedConfirmWorkCompletionContractCall();
                                    }}
                                    onTransactionConfirmed={(receipt) => {
                                        setWaitingForWallet(false);
                                        if (typeof onSuccess === 'function') {
                                            solutionService.confirmWorkCompletion(solution?.id);
                                            MetaPixel.track('Purchase', {
                                                value:    getTotalBudget(),
                                                currency: 'USD',
                                            });
                                        }
                                    }}
                                    onError={(error) => {
                                        setWaitingForWallet(false);
                                        //reset buttons
                                        showConfirmWorkCompletionActionButton(false);
                                        showTransactionConfirmMessageButton(true);

                                        if (typeof onError === 'function') {
                                            onError(error);
                                        }
                                    }}
                                >
                                    Confirm work completion
                                </TransactionButton>
                                <Button
                                    className='mt-2 mb-2 transaction-confirm-message'
                                    onClick={() => { setShowConfirmWorkCompletionNotice(true) }}
                                >
                                    <span>Confirm work completion</span>
                                </Button>
                                <label className='mt-1 w-100' style={{fontFamily: 'inter'}}>
                                    You will have an opportunity to cancel in the next step.
                                </label>                           
                                {
                                    waitingForWallet &&
                                    <Badge color="info" className='badge-outlined text-wrap text-start'>
                                        Waiting for {walletName ?? 'your wallet'} to confirm the transaction ...
                                    </Badge>
                                }
                            </div>
                            <ActionConfirm
                                visible={showConfirmWorkCompletionNotice}
                                message={
                                    'It appears the Contractor has completed the project, ' +
                                    'and you’re ready to confirm that the work meets your expectations. ' +
                                    'By clicking the “Continue” button below, you will authorize ' +
                                    'the release of funds from the Smart Contract to the Contractor’s wallet. ' +
                                    'Please note, this action is final and cannot be undone. ' +
                                    'If you need more time, click “Cancel” to return to the previous screen.'
                                }
                                onConfirm={() => {
                                    //hide message button
                                    showTransactionConfirmMessageButton(false);
                                    // show action button
                                    showConfirmWorkCompletionActionButton(true);
                                    // trigger transaction
                                    getElementByClassName('confirm-work-completion-action')?.click();
                                }}
                                onClose={() => { setShowConfirmWorkCompletionNotice(false) }}
                            />
                        </>
                    ) : (
                            <ConnectButton
                                className='w-100'
                                theme={'light'}
                                client={client}
                                wallets={wallets}
                                connectModal={{ size: "compact" }}
                                btnTitle='Connect your wallet'
                            />
                    )
                }
            </div>
        </Fragment>
    );
}